import React, { useState } from 'react'
import Route from 'route-parser'
import { MAILTO } from 'front-end-core/constants/urls'
import { URL_PDF_CA } from 'constants/urls'
import url from 'front-end-core/core/url'
import useObservable from 'front-end-core/hooks/useObservable'
import { isValidResponse } from 'front-end-core/helpers/dataflow'
import { getBaseURI } from 'front-end-core/helpers/document'
import { SHARE } from 'constants/controllers'
import cx from 'classnames'
import IconButton from '@material-ui/core/IconButton'
import Icon from 'components/shared/Icon'
import { toast } from 'react-toastify'
import notificationToast from 'helpers/notificationToast'
import LinkIcon from '@material-ui/icons/Link'
import Popover from '@material-ui/core/Popover'
import { TwitterShareButton, LinkedinShareButton, TwitterIcon, LinkedinIcon } from 'react-share'
import TealToolTip from 'components/TealToolTip'
import { htmlText } from 'helpers/htmlText'

const mailParser = new Route(MAILTO)

const ShareButton = ({ articleTitle, articleLink }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const handleClick = (event) => {
    event.preventDefault()
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => setAnchorEl(null)
  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <>
      <TealToolTip title='Share' placement='top'>
        <IconButton aria-label='Share' onClick={handleClick}>
          <Icon name='icon-share-variant' />
        </IconButton>
      </TealToolTip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <div className='flex share-buttons-container'>
          <TwitterButton link={articleLink} />
          <LinkedInButton link={articleLink} />
          <CopyLinkToClipboardButton link={articleLink} dataClipboardText={articleLink} />
        </div>
      </Popover>
    </>
  )
}

const TwitterButton = ({ link }) => (
  <TealToolTip title='Share on Twitter' placement='top'>
    <IconButton aria-label='Share with Twitter' data-event-category='Article' data-event-action='Share' data-event-label='Twitter'>
      <TwitterShareButton url={link} title={link}>
        <TwitterIcon size={32} round />
      </TwitterShareButton>
    </IconButton>
  </TealToolTip>
)

const LinkedInButton = ({ link }) => (
  <TealToolTip title='Share with Linkedin' placement='top'>
    <IconButton aria-label='Share on Linkedin' data-event-category='Article' data-event-action='Share' data-event-label='LinkedIn'>
      <LinkedinShareButton url={link}>
        <LinkedinIcon size={32} windowWidth={750} windowHeight={600} round />
      </LinkedinShareButton>
    </IconButton>
  </TealToolTip>
)

export const CopyLinkToClipboardButton = ({ link, size = 'medium', dataClipboardText, toolTipPlacement = 'top' }) => (
  <TealToolTip title='Copy link to clipboard' placement={toolTipPlacement}>
    <IconButton
      className='outline-0'
      {...{ size }}
      aria-label='Copy to clipboard'
      data-clipboard-text={dataClipboardText}
      data-event-category='Article'
      data-event-action='Share'
      data-event-label='Clipboard'
      onClick={() => {
        // clipboard js does not work when element is rendered in a Popover component
        navigator.clipboard.writeText(dataClipboardText)
        notificationToast(<LinkIcon />, toast.TYPE.INFO, 'Link copied to clipboard')
      }}
    >
      <div className='icon-clipboard-container cadet-blue'>
        <LinkIcon />
      </div>
    </IconButton>
  </TealToolTip>
)

const PrintArticleButton = ({ pdfUrl }) => {
  const handleOnClick = () => {
    const doc = window.open(pdfUrl)
    doc.print()
  }

  return (
    <div>
      <TealToolTip title='Print' placement='top'>
        <IconButton
          aria-label='Print'
          onClick={handleOnClick}
          data-event-category='Article'
          data-event-action='Print'
        >
          <Icon name='icon-printer-variant' />
        </IconButton>
      </TealToolTip>
    </div>
  )
}

const DownloadPdfButton = ({ pdfUrl }) => (
  <TealToolTip title='Download PDF' placement='top'>
    <IconButton
      download
      onClick={() => toast('PDF Downloading')}
      href={pdfUrl}
      aria-label='Download Pdf'
      data-event-category='Article'
      data-event-action='Download'
    >
      <Icon name='icon-pdf' />
    </IconButton>
  </TealToolTip>
)

const SendEmailButton = ({ articleLink }) => {
  const subject = 'FromCounsel - Shared content'
  const body = `A colleague has shared some FromCounsel content with you: ${articleLink}`
  const emailLink = 'mailto:' + mailParser.reverse({ subject, body }).replace('#', '%23')

  return (
    <TealToolTip title='Send Email' placement='top'>
      <IconButton
        onClick={() => window.open(emailLink, '_self')}
        aria-label='Email'
        data-event-category='Article'
        data-event-action='Share'
        data-event-label='Email'
      >
        <Icon name='icon-email-variant' />
      </IconButton>
    </TealToolTip>
  )
}

const IconButtons = ({ isLogged, stateSource, classNames, aol, title }) => {
  if (!isLogged) return null

  const stateData = useObservable(stateSource, { short: null }, [title])
  const { short } = stateData
  const articleLink = url(SHARE, { aol, short }, {}, true)
  const pdfUrl = (isValidResponse(short) && getBaseURI() + new Route(URL_PDF_CA).reverse({ aol, short })) || null

  return (
    <div className={cx('flex article-page-icons cadet-blue o-80', classNames)}>
      <ShareButton articleTitle={htmlText(title)} articleLink={articleLink} />
      <SendEmailButton {...{ articleLink }} />
      <PrintArticleButton {...{ pdfUrl }} />
      <DownloadPdfButton {...{ pdfUrl }} />
    </div>
  )
}

export default IconButtons
